import { Component, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectButtonModule } from 'primeng/selectbutton';

import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import { CurrencyService } from '../../core/services/currency.service';
import { OrderService } from '../../core/services/order.service';
import { StoreService } from '../../core/services/store.service';
import { DebtDto, DebtorDto, OrderDetailDto } from '../../core/models/order.models';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import {
  ReceiptComponent,
  ReceiptData,
  ReceiptPaymentType,
} from '../../shared/components/receipt/receipt.component';
import { ReceiptPrintService } from '../../shared/services/receipt-print.service';
import { ReceiptExportService } from '../../shared/services/receipt-export.service';
import { MoneyPipe } from '../../shared/pipes/money.pipe';

/**
 * Jadval qatori — `DebtorDto` + barqaror kalit.
 *
 * PrimeNG `dataKey` uchun obyektda HAQIQIY maydon kerak, hisoblangan funksiya
 * emas. Kalit mijoz+valyutadan iborat: ikki valyutada qarzi bor mijoz ikki qator
 * bo'lib chiqadi va faqat `customerId` ishlatilsa, ular bitta qator deb qabul
 * qilinib, biri ochilganda ikkinchisi ham ochilardi.
 */
type DebtorRow = DebtorDto & { rowKey: string };

@Component({
  selector: 'app-debtors',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    MultiSelectModule,
    TagModule,
    DialogModule,
    InputNumberModule,
    SelectButtonModule,
    TranslatePipe,
    InputTextModule,
    ReceiptComponent,
  ],
  templateUrl: './debtors.component.html',
})
export class DebtorsComponent implements OnInit {
  private orderSvc = inject(OrderService);
  private storeSvc = inject(StoreService);
  private readonly destroyRef = inject(DestroyRef);
  private authSvc = inject(AuthService);
  /** Shablon ham o'qiydi (kurs matni) — shuning uchun `private` emas. */
  readonly currencySvc = inject(CurrencyService);

  debtors = signal<DebtorRow[]>([]);
  totalCount = signal(0);
  loading = signal(false);
  pageSize = 20;

  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda filtr yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);
  filterStoreIds: string[] = [];
  searchTerm = signal('');

  expandedDebts = signal<Record<string, { loading: boolean; items: DebtDto[] }>>({});
  expandedRowKeys: Record<string, boolean> = {};

  payDialogVisible = false;
  payingSaving = false;
  payingDebt: DebtDto | null = null;

  /** Mijoz bergan summa — `payCurrency` da (qarz valyutasida emas). */
  payAmount = 0;

  /**
   * Pul QANDAY qabul qilingani: 0=Naqd, 1=Karta, 2=Bank. Kassa/karta/bank
   * qoldig'i shunga qarab hisoblanadi (`OrderPayments.Method`) — ilgari bu
   * yerda hech qanday tanlov yo'q edi va har bir qarz to'lovi doim NAQD deb
   * yozilardi, garchi mijoz kartadan yoki bankdan o'tkazgan bo'lsa ham.
   */
  payMethod: 0 | 1 | 2 = 0;

  payMethodOptions: { label: string; value: 0 | 1 | 2 }[] = [
    { label: 'SALES.PAYMENT.CASH', value: 0 },
    { label: 'SALES.PAYMENT.CARD', value: 1 },
    { label: 'FINANCE.METHOD.Bank', value: 2 },
  ];

  /**
   * Mijoz qaysi pulda to'layotgani. Qarz valyutasidan farq qilishi mumkin:
   * `$100` lik qarzni so'mda yopish normal holat. Qarzning O'ZI esa o'z
   * valyutasida qoladi — u qayta baholanmaydi.
   */
  payCurrency = '';

  /**
   * 1 birlik qarz valyutasi = shuncha `payCurrency`. Dialog ochilganda olinadi
   * va serverga AYNAN shu son yuboriladi — server uni qayta hisoblamaydi, aks
   * holda ekranda ko'rsatilgan summa bazadagidan farq qilardi.
   */
  payRate = 1;

  receiptData = signal<ReceiptData | null>(null);
  receiptOrderId = signal<string | null>(null);
  exportBusy = signal(false);
  shareBusy = signal(false);
  loadingReceipt = signal(false);
  private receiptPrinter = inject(ReceiptPrintService);
  private receiptExport = inject(ReceiptExportService);

  private lastLazyEvent: TableLazyLoadEvent = { first: 0, rows: this.pageSize };
  private storesLoaded = false;
  /** Employee uchun tayinlangan do'konlar — filter bo'shsa ham har doim shu filter qo'llanadi */
  private assignedStoreIds: string[] = [];

  ngOnInit() {
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const list = res.data ?? [];
          this.stores.set(list);
          if (this.authSvc.user()?.role === 'Employee') {
            this.assignedStoreIds = list.map((s) => s.id);
            this.filterStoreIds = [...this.assignedStoreIds];
          }
          this.storesLoaded = true;
          this.load(this.lastLazyEvent);
        },
      });
  }

  load(event: TableLazyLoadEvent) {
    this.lastLazyEvent = event;
    if (!this.storesLoaded) return;
    const page = Math.floor((event.first ?? 0) / (event.rows ?? this.pageSize)) + 1;
    const size = event.rows ?? this.pageSize;
    this.loading.set(true);
    // Employee filter bo'shatsa ham, tayinlangan do'konlari bo'yicha filter qoladi
    const storeIds = this.filterStoreIds.length
      ? this.filterStoreIds
      : this.assignedStoreIds.length
        ? this.assignedStoreIds
        : undefined;
    this.orderSvc
      .getDebtors(page, size, storeIds, this.searchTerm())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.debtors.set(
            (res.data?.items ?? []).map((d) => ({
              ...d,
              rowKey: DebtorsComponent.keyOf(d.customerId, d.currency),
            })),
          );
          this.totalCount.set(res.data?.totalCount ?? 0);
        },
        complete: () => this.loading.set(false),
      });
  }

  /**
   * Qator kaliti: mijoz + VALYUTA.
   *
   * Faqat `customerId` bo'lsa, ikki valyutada qarzi bor mijozning ikkala qatori
   * bitta kalitni bo'lishardi — birini ochganda ikkinchisi ham ochilib, ichida
   * aralash valyutadagi buyurtmalar chiqardi.
   */
  private static keyOf(customerId: string, currency: string): string {
    return `${customerId}|${currency}`;
  }

  toggleRow(debtor: DebtorRow, event: Event) {
    event.stopPropagation();
    const key = debtor.rowKey;
    const isExpanded = !!this.expandedRowKeys[key];

    if (isExpanded) {
      const copy = { ...this.expandedRowKeys };
      delete copy[key];
      this.expandedRowKeys = copy;
    } else {
      this.expandedRowKeys = { ...this.expandedRowKeys, [key]: true };
      this.onRowExpand(debtor);
    }
  }

  onRowExpand(debtor: DebtorRow) {
    const key = debtor.rowKey;
    this.expandedDebts.update((prev) => ({
      ...prev,
      [key]: { loading: true, items: [] },
    }));

    const storeIds = this.filterStoreIds.length
      ? this.filterStoreIds
      : this.assignedStoreIds.length
        ? this.assignedStoreIds
        : undefined;

    this.orderSvc
      // Valyuta ham uzatiladi — ichidagi buyurtmalar qator bilan bir xil
      // valyutada bo'lsin, aks holda jami mos kelmay qolardi.
      .getDebts(1, 100, storeIds, undefined, debtor.customerId, debtor.currency)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.expandedDebts.update((prev) => ({
            ...prev,
            [key]: {
              loading: false,
              items: res.data?.items ?? [],
            },
          }));
        },
        error: () => {
          this.expandedDebts.update((prev) => ({
            ...prev,
            [key]: { loading: false, items: [] },
          }));
        },
      });
  }

  /**
   * To'lovdan keyin ochiq qatorni yangilaydi.
   *
   * Valyuta ham kerak: kalit mijoz+valyuta, va yangilanadigan ro'yxat aynan
   * to'lov qilingan valyutadagi qarzlar bo'lishi kerak.
   */
  reloadExpandedDebts(customerId: string, currency: string) {
    const key = DebtorsComponent.keyOf(customerId, currency);
    const storeIds = this.filterStoreIds.length
      ? this.filterStoreIds
      : this.assignedStoreIds.length
        ? this.assignedStoreIds
        : undefined;

    this.orderSvc
      .getDebts(1, 100, storeIds, undefined, customerId, currency)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.expandedDebts.update((prev) => ({
            ...prev,
            [key]: {
              loading: false,
              items: res.data?.items ?? [],
            },
          }));
        },
        error: () => {
          this.expandedDebts.update((prev) => ({
            ...prev,
            [key]: { loading: false, items: [] },
          }));
        },
      });
  }

  onFilterChange() {
    this.load({ first: 0, rows: this.pageSize });
  }

  onSearchChange(value: string) {
    this.searchTerm.set(value);
    this.load({ first: 0, rows: this.pageSize });
  }

  remaining(debt: DebtDto): number {
    return Math.max(0, debt.totalAmount - debt.paidAmount);
  }

  // ── To'lov valyutasi ────────────────────────────────────────────────────────

  /** Qarz valyutasi — qoldiq va jami shu valyutada. */
  get debtCurrency(): string {
    return this.payingDebt?.currency || this.currencySvc.defaultCode();
  }

  /**
   * Tanlash mumkin bo'lgan valyutalar: qarz valyutasi + kursi MA'LUM bo'lganlari.
   *
   * Kursi yo'q valyuta ro'yxatga tushmaydi — taxminiy kurs bilan yopilgan qarzni
   * keyin hech kim to'g'rilay olmaydi (server ham uni rad etadi).
   */
  get payCurrencyOptions(): { label: string; value: string }[] {
    const debtCode = this.debtCurrency;
    const options = [{ label: debtCode, value: debtCode }];

    for (const currency of this.currencySvc.activeCurrencies()) {
      if (currency.code === debtCode) continue;
      if (this.currencySvc.rate(debtCode, currency.code) === null) continue;
      options.push({ label: currency.code, value: currency.code });
    }

    return options;
  }

  get isCrossCurrencyPayment(): boolean {
    return this.payCurrency !== this.debtCurrency;
  }

  /** AYNAN shu juftlikning kursi eskirganmi (qizil ogohlantirish). */
  get isPayRateStale(): boolean {
    return this.currencySvc.isRateStale(this.debtCurrency, this.payCurrency);
  }

  /**
   * Qarz qoldig'ining TO'LOV valyutasidagi qiymati — undirish mumkin bo'lgan
   * eng ko'p summa. Kiritilgan raqam ham shu valyutada, ya'ni taqqoslash to'g'ri
   * bo'ladi (qoldiq bilan solishtirish `$` ni so'm bilan solishtirish bo'lardi).
   */
  get maxPayable(): number {
    if (!this.payingDebt) return 0;
    const remaining = this.remaining(this.payingDebt);
    if (!this.isCrossCurrencyPayment) return remaining;
    // `round` EMAS: bu QARZ CHEGARASI. Qadamga tortilsa yuqoriga yaxlitlanib
    // ortiqcha undirishga ruxsat berardi, pastga tushsa esa mijoz qarzini
    // to'liq yopa olmasdi (bir necha tiyin osilib qolardi).
    return this.currencySvc.roundPrice(remaining * this.payRate, this.payCurrency);
  }

  /** To'lov summasi yaroqlimi — tasdiqlash tugmasi shu bilan yoqiladi. */
  get isPayAmountValid(): boolean {
    return this.payAmount > 0 && this.payAmount <= this.maxPayable;
  }

  /** Chegaradan oshib ketganmi (maydonni qizil qilish uchun). */
  get isPayAmountOverMax(): boolean {
    return this.payAmount > this.maxPayable;
  }

  onPayCurrencyChange(): void {
    this.refreshPayRate();
    // Summa eski valyutada edi — yangi chegaraga tenglashtiramiz, aks holda
    // `1 265 000` `$1 265 000` bo'lib qolardi.
    this.payAmount = this.maxPayable;
  }

  private refreshPayRate(): void {
    if (!this.isCrossCurrencyPayment) {
      this.payRate = 1;
      return;
    }

    const rate = this.currencySvc.rate(this.debtCurrency, this.payCurrency);
    if (rate === null || rate <= 0) {
      // Kurs yo'q — taxmin qilmaymiz, qarz valyutasiga qaytamiz. 1 bilan davom
      // etish `$100` lik qarzni `100 so'm` ga yopib qo'yardi.
      this.payCurrency = this.debtCurrency;
      this.payRate = 1;
      return;
    }

    this.payRate = rate;
  }

  /** Qarz muddati o'tib ketganmi (bugundan oldin). */
  isOverdue(dueDate: string | null): boolean {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  }

  openPayDialog(debt: DebtDto) {
    this.payingDebt = debt;
    // Default — qarz o'z valyutasida to'lanadi; kassir uni almashtira oladi.
    this.payCurrency = debt.currency || this.currencySvc.defaultCode();
    this.payRate = 1;
    this.payAmount = this.remaining(debt);
    this.payMethod = 0;
    this.payDialogVisible = true;

    this.loadingReceipt.set(true);
    this.receiptData.set(null);
    this.receiptOrderId.set(debt.id);
    // PDF darhol olinadi — "Ulashish" bosilganda `navigator.share()` bosish
    // aktivatsiyasi ichida sinxron chaqirilishi kerak (servisga qarang).
    this.receiptExport.prefetch(debt.id);
    this.orderSvc
      .getById(debt.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.data) {
            this.receiptData.set(this.toReceiptData(res.data));
          }
          this.loadingReceipt.set(false);
        },
        error: () => {
          this.loadingReceipt.set(false);
        },
      });
  }

  closePayDialog() {
    this.payDialogVisible = false;
    this.payingDebt = null;
    this.payAmount = 0;
    this.payCurrency = '';
    this.payRate = 1;
    this.payMethod = 0;
    this.receiptData.set(null);
    this.receiptOrderId.set(null);
  }

  printReceipt(): void {
    this.receiptPrinter.print();
  }

  downloadReceiptPdf(): void {
    const orderId = this.receiptOrderId();
    if (!orderId || this.exportBusy()) return;

    this.exportBusy.set(true);
    this.receiptExport
      .downloadPdf(orderId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.exportBusy.set(false)),
      )
      .subscribe();
  }

  shareReceipt(): void {
    const orderId = this.receiptOrderId();
    if (!orderId || this.shareBusy()) return;

    this.shareBusy.set(true);
    this.receiptExport
      .sharePdf(orderId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.shareBusy.set(false)),
      )
      .subscribe();
  }

  private toReceiptData(order: OrderDetailDto): ReceiptData {
    const paymentTypes: Record<string, ReceiptPaymentType> = { Cash: 0, Card: 1, Debt: 2 };
    const subtotal = order.items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
    const discount = order.items.reduce((sum, item) => sum + item.discount * item.quantity, 0);
    const paymentType = paymentTypes[order.paymentType] ?? 0;

    return {
      receiptNumber: order.receiptNumber ?? order.id,
      storeName: order.storeName,
      cashierName: order.cashierName,
      date: order.orderDate,
      items: order.items.map((item, index) => ({
        id: `${item.productId}-${item.productVariantId ?? ''}-${index}`,
        productId: item.productId,
        productVariantId: item.productVariantId,
        productName: item.variantLabel
          ? `${item.productName} (${item.variantLabel})`
          : item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discount: item.discount,
      })),
      subtotal,
      discount,
      total: order.totalAmount,
      paymentType,
      paidAmount: order.paidAmount,
      change: 0,
      // Qayta chop etishda to'lov valyutasi order valyutasi bilan bir xil deb
      // olinadi: `Order` da to'lov valyutasi saqlanmaydi, u `OrderPayment` da.
      // To'lov tafsiloti kerak bo'lsa Bosqich 4c da chekka qo'shiladi.
      currency: order.currency,
      payCurrency: order.currency,
      payRate: 1,
      debtCustomerName: order.debtCustomerName,
    };
  }

  confirmPayment() {
    if (!this.payingDebt || this.payAmount <= 0) {
      this.orderSvc.showWarn("To'lov summasi noto'g'ri");
      return;
    }

    // Chegara tugma darajasida ham tekshiriladi, lekin bu yerda takrorlaymiz:
    // valyuta almashtirilganda chegara o'zgaradi va eski summa oshib ketishi mumkin.
    if (this.payAmount > this.maxPayable) {
      this.orderSvc.showWarn(
        `Qarzdan ko'p — maksimal ${this.currencySvc.format(this.maxPayable, this.payCurrency)}`,
      );
      return;
    }

    const cashierId = this.authSvc.user()?.id ?? '';
    const customerId = this.payingDebt.customerId;
    // Qaysi qatorni yangilashni to'lovdan OLDIN eslab qolamiz — `closePayDialog`
    // `payingDebt` ni tozalaydi.
    const debtCurrency = this.debtCurrency;
    this.payingSaving = true;
    this.orderSvc
      .payDebt(
        this.payingDebt.id,
        this.payAmount,
        cashierId,
        this.payCurrency,
        // Ekranda ko'rsatilgan kurs — server uni qayta hisoblamaydi.
        this.isCrossCurrencyPayment ? this.payRate : undefined,
        this.payMethod,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.orderSvc.showSuccess("To'lov amalga oshirildi");
          this.payingSaving = false;
          this.closePayDialog();
          this.load(this.lastLazyEvent);
          if (customerId) {
            this.reloadExpandedDebts(customerId, debtCurrency);
          }
        },
        error: () => {
          this.payingSaving = false;
        },
      });
  }
}
